import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiServices from "../../service";
import { applyCompanyCurrencySettings } from "../../utils/currency";

// The company profile carries the three Currency Display Settings (Region,
// Currency, DecimalDigit), and every formatted amount in the app reads them
// from utils/currency's module state. Applying them here rather than in a
// component is what makes "call the setters once at bootstrap" true: ASCLayout
// already fetches this profile on every authenticated page, so the settings are
// in place before any grid, panel or form renders an amount. Doing it in the
// update thunk too means a save takes effect without a reload.
//
// Reducers stay pure — this is a thunk, which is where side effects belong.

const initialState: any = {
  data: null,
  loading: false,
  saveLoading: false,
};

export const getCompanyDetails = createAsyncThunk(
  "companyDetails/get",
  async (_, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.systemManager.companyDetails.get();
      if (response.data?.isError) {
        return rejectWithValue(
          response.data?.error?.message?.[0] || "Failed to fetch company details"
        );
      }
      applyCompanyCurrencySettings(response.data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error?.message?.[0] || error.message || "An error occurred"
      );
    }
  }
);

export const updateCompanyDetails = createAsyncThunk(
  "companyDetails/update",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response: any = await apiServices.systemManager.companyDetails.update(payload);
      if (response.data?.isError) {
        return rejectWithValue(
          response.data?.error?.message?.[0] || "Failed to save company details"
        );
      }
      applyCompanyCurrencySettings(response.data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error?.message?.[0] || error.message || "An error occurred"
      );
    }
  }
);

const companyDetailsSlice = createSlice({
  name: "companyDetails",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCompanyDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCompanyDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getCompanyDetails.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateCompanyDetails.pending, (state) => {
        state.saveLoading = true;
      })
      .addCase(updateCompanyDetails.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.data = action.payload;
      })
      .addCase(updateCompanyDetails.rejected, (state) => {
        state.saveLoading = false;
      });
  },
});

export default companyDetailsSlice.reducer;
